import { apiFiles } from "@/api/file.api.ts";
import { FileItem } from "@/components/files/FileItem.tsx";
import useErrorHandle from "@/hooks/use-error-handle.hook.tsx";
import { useFileDropzone } from "@/hooks/use-file-dropzone.hook.ts";
import { ImportDataPreviewTable } from "@/pages/settings/import-data/edit/components/ImportDataPreviewTable.tsx";
import { useImportDataEditContext } from "@/pages/settings/import-data/edit/import-data-edit.types.ts";
import { useImportDataPreview } from "@/queries/import-data.query.ts";
import { ApiFilePathTypes } from "@/types/file.types.ts";
import { ApiImportDataType } from "@/types/import-data.types.ts";
import {
  Avatar,
  Box,
  Button,
  ButtonLink,
  FileDropzone,
  ListItem,
  Stack,
  Text,
  Title,
  useToasts,
} from "@skailer-com/uikit";
import { useCallback, useEffect, useLayoutEffect, useState } from "react";
import { useTranslation } from "react-i18next";

const ImportFileTypes = {
  xlsx: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  csv: "text/csv",
};

const importTemplates: Partial<Record<ApiImportDataType, string>> = {
  [ApiImportDataType.employee]: "/import-data-examples/People import template.xlsx",
  [ApiImportDataType.compensation]: "/import-data-examples/Compensation import template.xlsx",
  [ApiImportDataType.additional_compensation]:
    "/import-data-examples/Additional compensation import template.xlsx",
  [ApiImportDataType.employment]: "/import-data-examples/Employment import template.xlsx",
  [ApiImportDataType.position]: "/import-data-examples/Work import template.xlsx",
  [ApiImportDataType.time_off_balances]:
    "/import-data-examples/Time off balances import template.xlsx",
  [ApiImportDataType.time_off_history]:
    "/import-data-examples/Time off history import template.xlsx",
};

export function ImportDataEditStepPreview() {
  const {
    file,
    setFile,
    setImportId,
    setRowsCount,
    goToNextStep,
    registerOnNext,
    nextButtonText,
    importType,
  } = useImportDataEditContext();
  const { showInfo } = useToasts();
  const { t } = useTranslation();
  const { handleErrors } = useErrorHandle();
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const { data: previewOverview, isLoading: isPreviewLoading } = useImportDataPreview(file?.uuid);
  const supportedFormats = Object.keys(ImportFileTypes).join(", ").toUpperCase();

  useEffect(() => {
    if (previewOverview) {
      setImportId(previewOverview.import_id);
      setRowsCount(previewOverview.preview.rows_count);
    }
  }, [previewOverview]);

  const handleNext = useCallback(() => {
    if (!previewOverview) {
      showInfo({ title: t("import_data:upload_file_first") });
      return;
    }
    goToNextStep();
  }, [previewOverview, goToNextStep, showInfo, t]);

  useLayoutEffect(() => {
    registerOnNext(handleNext);
    return () => registerOnNext(null);
  }, [handleNext, registerOnNext]);

  const onDrop = useCallback(
    async (acceptedFiles: File[]) => {
      if (isUploading || isPreviewLoading) return;

      if (acceptedFiles.length > 0) {
        const file = acceptedFiles[0];
        const formData = new FormData();
        formData.append("file", file);
        formData.append("path", ApiFilePathTypes.data_import);
        formData.append("save_original_name", "true");

        setIsUploading(true);

        const { data, error } = await apiFiles.add(formData);

        if (data) {
          setFile(data);
        } else {
          handleErrors(error);
        }

        setIsUploading(false);
      }
    },
    [handleErrors]
  );

  const { onDropRejected } = useFileDropzone({
    tooManyFilesMessage: t("errors:only_one_file_allowed", {
      formats: supportedFormats,
    }),
    unsupportedFileFormatMessage: t("errors:unsupported_file_format", {
      formats: supportedFormats,
    }),
  });

  return (
    <Stack gap={"sm"}>
      <Stack className={"card-panel"}>
        <Title
          size={"lg"}
          header={t("import_data:create_new_import")}
          caption={t("import_data:import_data_to_system")}
        />
      </Stack>

      <Stack className={"card-panel"} gap={"2xl"}>
        <Title
          header={t("import_data:upload_file_title")}
          caption={t("import_data:upload_file_caption")}
        />
        <Stack>
          {importType && importTemplates[importType] && (
            <ListItem
              className={"border-0"}
              emoji={":mortar_board:"}
              title={t("import_data:download_template_title")}
              caption={t("import_data:download_template_caption")}
              valueSlot={
                <ButtonLink
                  target={"_blank"}
                  href={importTemplates[importType]}
                  variant={"secondary"}
                >
                  {t("common:download")}
                </ButtonLink>
              }
            />
          )}
          {file && !isPreviewLoading ? (
            <Stack
              className={"border-dark/5 bg-dark/5 rounded-xs text-center p-9 cursor-pointer"}
              justify={"center"}
              items={"center"}
              gap={"md"}
            >
              <Stack gap={"sm"} justify={"center"} items={"center"}>
                <Avatar size={"xl"} emoji={":cucumber:"}></Avatar>
                <Stack>
                  <Text className={"text-title text-lg"}>
                    {t("import_data:file_uploaded_ready")}
                  </Text>
                  <Text>
                    {t("import_data:found_columns", {
                      count_columns: t("plurals:columns", {
                        count: previewOverview?.preview.columns.length,
                      }),
                    })}
                  </Text>
                </Stack>
              </Stack>

              <Box className={"max-w-full"}>
                <FileItem
                  file={file}
                  onRemove={() => {
                    setFile(null);
                  }}
                />
              </Box>
            </Stack>
          ) : (
            <FileDropzone
              onDrop={onDrop}
              accept={Object.fromEntries(
                Object.entries(ImportFileTypes).map(([key, value]) => [value, [`.${key}`]])
              )}
              onDropRejected={onDropRejected}
              header={<Avatar size={"xl"} emoji={":tophat:"} />}
              description={t("common:type_files_supported", {
                types: ["CSV", "XLSX"].join(", "),
              })}
              ctaActionText={t("common:file").toLowerCase()}
              descriptionSize={"sm"}
              activeDescription={t("common:upload_file_drag_and_drop")}
              isLoading={isUploading || isPreviewLoading}
              multiple={false}
              maxFiles={1}
            />
          )}
        </Stack>

        {previewOverview && <ImportDataPreviewTable previewOverview={previewOverview} />}
      </Stack>

      <Button size={"lg"} onClick={handleNext}>
        {nextButtonText}
      </Button>
    </Stack>
  );
}
